import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import ReservationService from '~/services/ReservationService';

import { StopsellApplyPayload, StopsellApplyResponse } from '~/types/stopsell';

export function useApplyBlackoutDates(propertyId: string, sourceLabel = 'stopsell dates') {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (payload: StopsellApplyPayload): Promise<StopsellApplyResponse> =>
      ReservationService.applyBlackoutDatesBulk(propertyId, payload),
    onSuccess: (response) => {
      const succeededCount = response.result.succeeded.length;
      const failedCount = response.result.failed.length;

      if (succeededCount > 0) {
        queryClient.invalidateQueries({ queryKey: ['blackoutDates', propertyId] });
      }

      if (failedCount === 0) {
        enqueueSnackbar(`${sourceLabel[0].toUpperCase()}${sourceLabel.slice(1)} applied successfully`, {
          variant: 'success',
        });
        return;
      }

      const singularSourceLabel = sourceLabel.replace(/s$/, '');
      const firstFailure = response.result.failed[0];
      const firstFailureMessage = firstFailure ? ` First error: ${firstFailure.error}` : '';
      const succeededLabel = `${succeededCount} ${singularSourceLabel} ${succeededCount === 1 ? 'range' : 'ranges'}`;
      const failedLabel = `${failedCount} ${failedCount === 1 ? 'range' : 'ranges'} failed`;
      const message =
        succeededCount > 0
          ? `Applied ${succeededLabel}. ${failedLabel}.${firstFailureMessage}`
          : `Failed to apply ${sourceLabel}. ${failedLabel}.${firstFailureMessage}`;

      enqueueSnackbar(message, { variant: succeededCount > 0 ? 'warning' : 'error' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to apply ${sourceLabel}: ${error.message}`, { variant: 'error' });
    },
  });
}
